
import React, { JSX, ReactNode, useEffect, useState } from 'react'
import { Navigate } from 'react-router'
import {
  CheckIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ErrorInline } from 'components/ErrorInline'
import { LoadingSpinner } from 'components/Loading'
import { useAuth } from 'context/user'
import { useEntitlements, tierLabel, planDisplayName } from 'context/plan'
import { useV1Visible } from 'hooks/useV1Visible'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { QUICK_OVERLAYS_PATH } from 'lib/paths'
import { Plan, PlanLimitKey } from 'types/api'
import createCheckoutSessionMutation from 'graphql/createCheckoutSession.graphql'
import createPortalSessionMutation from 'graphql/createPortalSession.graphql'

const MIB = 1024 * 1024
const GIB = 1024 * MIB

const round1 = (n: number): string => {
  const fixed = n.toFixed(1)
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed
}

const formatBytes = (bytes: number): string =>
  bytes >= GIB ? `${round1(bytes / GIB)} GB` : `${round1(bytes / MIB)} MB`

const LIMIT_ROWS: Array<{ key: PlanLimitKey, label: string, format?: (n: number) => string }> = [
  { key: 'eventsPerAccount', label: 'Streams' },
  { key: 'chatOverlaysPerAccount', label: 'Chat overlays' },
  { key: 'emoteWallsPerAccount', label: 'Emote walls' },
  { key: 'twitchChannelsPerOverlay', label: 'Twitch channels per overlay' },
  { key: 'assetStorageBytes', label: 'Asset storage', format: formatBytes },
  { key: 'interactiveElementsPerStream', label: 'Interactive elements per stream' },
  { key: 'voteChannelsPerElement', label: 'Vote channels per element' }
]

const INTERACTIVE_CONTENT_LABEL = 'Interactive content: polls, tug-of-wars and scoreboards'

const capLabel = (cap: number | null, format?: (n: number) => string): string =>
  cap === null ? 'Unlimited' : (format ?? String)(cap)

interface CheckoutSession {
  url: string
}

const CHECKOUT_UNAVAILABLE = "Billing isn't available yet, check back soon."

const CHECKOUT_ALREADY_SUBSCRIBED =
  'You already have an active subscription, use the Manage billing ' +
  'button to change plans. Upgrades are charged as a prorated difference.'

const ALREADY_SUBSCRIBED = /active subscription/i

interface PortalSession {
  url: string
}

type PortalIssue = 'no-self-serve' | 'unavailable' | null

const NO_BILLING_ON_FILE = /no (stripe )?(billing|customer)|billing on file|customer on file/i

const PORTAL_NO_SELF_SERVE =
  'Your plan was set up for you by our team, so there are no billing ' +
  'details to manage here. Contact support if you’d like to change or ' +
  'cancel it.'

const PORTAL_UNAVAILABLE =
  "Billing management isn't available right now, please try again soon."

const SHOPIFY_BILLING_MANAGED =
  'Billing for this account is managed through the Shopify App Store.'

type CheckoutReturn = 'success' | 'cancelled' | null

type PortalBusySource = 'manage' | 'resubscribe' | 'downgrade-free' | `plan-${string}`

interface PlanCardCta {
  label: string
  busy: boolean
  disabled: boolean
  busyLabel: string
  title?: string
  testId: string
  handleClick: () => void
}

const UsageMeter = ({
  label,
  used,
  cap,
  format,
  testId
}: {
  label: string
  used: number
  cap: number | null
  format?: (n: number) => string
  testId: string
}): JSX.Element => {
  const pct = cap !== null && cap > 0
    ? Math.min(100, Math.round((used / cap) * 100))
    : null

  return (
    <div data-testid={testId}>
      <div className='flex items-baseline justify-between gap-3'>
        <span className='text-sm text-dm-text-secondary'>{label}</span>
        <span className='text-sm text-dm-text-primary'>
          {(format ?? String)(used)} <span className='text-dm-text-accent'>/ {capLabel(cap, format)}</span>
        </span>
      </div>
      {pct !== null && (
        <div className='mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/5'>
          <div className='h-full rounded-full bg-brand' style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  )
}

const PlanCard = ({ plan, current, cta }: {
  plan: Plan
  current: boolean
  cta: PlanCardCta | null
}): JSX.Element => (
  <div
    data-testid={`billing-plan-${plan.tier}`}
    className={classNames(
      'flex flex-col rounded-lg border p-4',
      current
        ? 'border-brand/60 bg-brand/5'
        : 'border-white/10 bg-dm-bg-secondary'
    )}
  >
    <div className='flex items-center justify-between gap-2'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{planDisplayName(plan)}</h3>
      {current && (
        <span className='rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand'>
          Your plan
        </span>
      )}
      {!current && !plan.purchasable && (
        <span className='rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
          Contact us
        </span>
      )}
    </div>
    <ul className='mt-3 flex flex-col gap-1.5'>
      {LIMIT_ROWS.map(({ key, label, format }) => (
        <li key={key} className='flex items-baseline justify-between gap-3 text-sm'>
          <span className='text-dm-text-secondary'>{label}</span>
          <span className='text-dm-text-primary'>{capLabel(plan.limits[key], format)}</span>
        </li>
      ))}
      <li className='mt-1 flex items-start gap-2 border-t border-white/5 pt-2 text-sm'>
        {plan.limits.interactiveContent
          ? <CheckIcon className='mt-0.5 size-4 shrink-0 text-brand' aria-hidden='true' />
          : <XMarkIcon className='mt-0.5 size-4 shrink-0 text-dm-text-accent' aria-hidden='true' />}
        <span className={plan.limits.interactiveContent ? 'text-dm-text-primary' : 'text-dm-text-accent'}>
          {INTERACTIVE_CONTENT_LABEL}
        </span>
      </li>
    </ul>
    {cta !== null && (
      <div className='mt-auto pt-4'>
        <button
          type='button'
          onClick={cta.handleClick}
          disabled={cta.busy || cta.disabled}
          title={cta.title}
          data-testid={cta.testId}
          className='inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {cta.busy ? cta.busyLabel : cta.label}
        </button>
      </div>
    )}
  </div>
)

const BillingShell = ({ children }: { children: ReactNode }): JSX.Element => (
  <div className='flex flex-1' data-testid='billing-page'>
    <main className='flex flex-1 flex-col px-4 py-4'>
      <header className='mb-6 flex items-center gap-3'>
        <h1 className='text-2xl font-semibold text-dm-text-primary'>Subscription</h1>
      </header>
      {children}
    </main>
  </div>
)

export const BillingPage = (): JSX.Element => {
  const { auth0Token } = useAuth()
  const {
    status, error, tier, staffUncapped, shopifyBilled,
    limitFor, usage, plans, refetch, account
  } = useEntitlements()
  const v1Visible = useV1Visible()

  const [checkoutBusyTier, setCheckoutBusyTier] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [checkoutReturn, setCheckoutReturn] = useState<CheckoutReturn>(null)
  const [portalBusySource, setPortalBusySource] = useState<PortalBusySource | null>(null)
  const [portalIssue, setPortalIssue] = useState<PortalIssue>(null)

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search)
    const returned = params.get('checkout')
    if (returned !== 'success' && returned !== 'cancelled') return
    setCheckoutReturn(returned)
    if (returned === 'success') void refetch()
    params.delete('checkout')
    const rest = params.toString()
    globalThis.history.replaceState(
      null, '', globalThis.location.pathname + (rest.length > 0 ? `?${rest}` : '')
    )
  }, [refetch])

  const handleUpgrade = async (planTier: string): Promise<void> => {
    if (shopifyBilled) return
    if (auth0Token === undefined || checkoutBusyTier !== null) return
    setCheckoutBusyTier(planTier)
    setCheckoutError(null)
    try {
      const { checkout } = await graphqlRequest<{ checkout: CheckoutSession }>(
        createCheckoutSessionMutation,
        { tier: planTier, returnOrigin: globalThis.location.origin },
        auth0Token
      )
      globalThis.location.assign(checkout.url)
    } catch (err) {
      setCheckoutError(
        err instanceof Error && ALREADY_SUBSCRIBED.test(err.message)
          ? CHECKOUT_ALREADY_SUBSCRIBED
          : CHECKOUT_UNAVAILABLE
      )
      setCheckoutBusyTier(null)
    }
  }

  const handleManageBilling = async (source: PortalBusySource): Promise<void> => {
    if (shopifyBilled) return
    if (auth0Token === undefined || portalBusySource !== null || checkoutBusyTier !== null) return
    setPortalBusySource(source)
    setPortalIssue(null)
    try {
      const { portal } = await graphqlRequest<{ portal: PortalSession }>(
        createPortalSessionMutation,
        { returnOrigin: globalThis.location.origin },
        auth0Token
      )
      globalThis.location.assign(portal.url)
    } catch (err) {
      setPortalIssue(
        err instanceof Error && NO_BILLING_ON_FILE.test(err.message)
          ? 'no-self-serve'
          : 'unavailable'
      )
      setPortalBusySource(null)
    }
  }
  if (!v1Visible) return <Navigate to={QUICK_OVERLAYS_PATH} replace />

  if (status === 'loading') {
    return (
      <BillingShell>
        <div data-testid='billing-loading' className='flex flex-1 items-center justify-center'>
          <LoadingSpinner />
        </div>
      </BillingShell>
    )
  }

  if (status === 'error') {
    return (
      <BillingShell>
        <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-6'>
          <div
            data-testid='billing-error'
            className='flex flex-col items-center justify-center gap-3 py-12 text-center'
          >
            <ExclamationTriangleIcon className='size-8 text-red-400' aria-hidden='true' />
            <p className='max-w-sm text-sm text-red-300'>
              {error ?? 'Could not load your plan.'}
            </p>
            <button
              type='button'
              onClick={() => { void refetch() }}
              data-testid='billing-retry'
              className='rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              Retry
            </button>
          </div>
        </section>
      </BillingShell>
    )
  }

  const offerManageBilling = status === 'ready' && !staffUncapped &&
    !shopifyBilled && tier !== null && tier !== 'free'

  const paidPlanChangesViaPortal = offerManageBilling

  const cancelScheduled = !shopifyBilled && account?.cancelAtPeriodEnd === true
  const periodEnd = account?.currentPeriodEnd ?? null

  return (
    <BillingShell>
      <div className='flex flex-col gap-6'>
        {!shopifyBilled && checkoutReturn === 'success' && (
          <div
            data-testid='billing-checkout-success'
            className='flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300'
          >
            <CheckIcon className='size-4 shrink-0' aria-hidden='true' />
            <span className='flex-1'>
              Payment received, thank you! Activation may take a moment, so if
              your new plan isn’t showing below yet, refresh in a few seconds.
            </span>
            <button
              type='button'
              onClick={() => setCheckoutReturn(null)}
              aria-label='Dismiss'
              title='Dismiss'
              className='-m-1 shrink-0 rounded p-1 text-emerald-300/70 transition-colors hover:bg-emerald-500/10 hover:text-emerald-200'
            >
              <XMarkIcon className='size-3.5' aria-hidden='true' />
            </button>
          </div>
        )}
        {!shopifyBilled && checkoutReturn === 'cancelled' && (
          <div
            data-testid='billing-checkout-cancelled'
            className='flex items-start gap-2 rounded-md border border-white/10 bg-dm-bg-secondary p-3 text-xs text-dm-text-secondary'
          >
            <span className='flex-1'>
              Checkout was cancelled, no charge was made. You can upgrade any
              time from the plans below.
            </span>
            <button
              type='button'
              onClick={() => setCheckoutReturn(null)}
              aria-label='Dismiss'
              title='Dismiss'
              className='-m-1 shrink-0 rounded p-1 text-dm-text-accent transition-colors hover:bg-white/5 hover:text-dm-text-primary'
            >
              <XMarkIcon className='size-3.5' aria-hidden='true' />
            </button>
          </div>
        )}

        <section
          data-testid='billing-current-plan'
          className='rounded-xl border border-white/5 bg-dm-bg-secondary p-6'
        >
          <h2 className='mb-2 text-sm font-semibold uppercase tracking-wider text-dm-text-accent'>
            Current plan
          </h2>
          {staffUncapped
            ? (
              <p className='text-sm text-dm-text-secondary'>
                This is an internal staff account: no plan caps apply to it.
              </p>
              )
            : (
              <p className='text-sm text-dm-text-secondary'>
                You are on{' '}
                <span className='font-semibold text-dm-text-primary'>
                  {tier !== null ? tierLabel(tier) : '—'}
                </span>.
              </p>
              )}
          {cancelScheduled && (
            <div
              data-testid='billing-cancel-scheduled'
              className='mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3'
            >
              <p className='min-w-0 text-xs text-amber-300'>
                {periodEnd !== null
                  ? `Your plan expires ${formatLocalDateTime(periodEnd)}.`
                  : 'Your plan is set to expire at the end of the current billing period.'}
              </p>
              <button
                type='button'
                onClick={() => { void handleManageBilling('resubscribe') }}
                disabled={portalBusySource !== null || checkoutBusyTier !== null}
                data-testid='billing-resubscribe'
                className='inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amber-400 px-3 py-1.5 text-xs font-semibold text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60'
              >
                {portalBusySource === 'resubscribe' ? 'Opening billing portal…' : 'Resubscribe'}
              </button>
            </div>
          )}
          {offerManageBilling && (
            <div className='mt-4'>
              <button
                type='button'
                onClick={() => { void handleManageBilling('manage') }}
                disabled={portalBusySource !== null || checkoutBusyTier !== null}
                data-testid='billing-manage'
                className='rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
              >
                {portalBusySource === 'manage' ? 'Opening billing portal…' : 'Manage billing'}
              </button>
              <p className='mt-2 text-xs text-dm-text-accent'>
                Update your payment details or cancel your subscription
                through Stripe’s secure billing portal.
              </p>
            </div>
          )}
          {shopifyBilled && (
            <div
              data-testid='billing-shopify-managed'
              className='mt-4 max-w-2xl rounded-md border border-white/10 bg-dm-bg-primary p-3 text-xs text-dm-text-secondary'
            >
              {SHOPIFY_BILLING_MANAGED}
            </div>
          )}
          {portalIssue === 'no-self-serve' && (
            <div
              data-testid='billing-manage-no-self-serve'
              className='mt-3 flex max-w-2xl items-start gap-2 rounded-md border border-white/10 bg-dm-bg-primary p-3 text-xs text-dm-text-secondary'
            >
              <span className='flex-1'>{PORTAL_NO_SELF_SERVE}</span>
              <button
                type='button'
                onClick={() => setPortalIssue(null)}
                aria-label='Dismiss'
                title='Dismiss'
                className='-m-1 shrink-0 rounded p-1 text-dm-text-accent transition-colors hover:bg-white/5 hover:text-dm-text-primary'
              >
                <XMarkIcon className='size-3.5' aria-hidden='true' />
              </button>
            </div>
          )}
          {portalIssue === 'unavailable' && (
            <div className='mt-3 max-w-2xl' data-testid='billing-manage-error'>
              <ErrorInline onDismiss={() => setPortalIssue(null)}>
                {PORTAL_UNAVAILABLE}
              </ErrorInline>
            </div>
          )}
        </section>

        <section
          data-testid='billing-usage'
          className='rounded-xl border border-white/5 bg-dm-bg-secondary p-6'
        >
          <h2 className='mb-2 text-sm font-semibold uppercase tracking-wider text-dm-text-accent'>
            Usage
          </h2>
          <p className='text-xs text-dm-text-accent'>
            What your plan includes, and how much of it you have used so far.
          </p>
          <div className='mt-4 flex max-w-md flex-col gap-4'>
            <UsageMeter
              label='Streams'
              used={usage.events}
              cap={limitFor('eventsPerAccount')}
              testId='billing-usage-events'
            />
            <UsageMeter
              label='Chat overlays'
              used={usage.chatOverlays}
              cap={limitFor('chatOverlaysPerAccount')}
              testId='billing-usage-chat-overlays'
            />
            <UsageMeter
              label='Asset storage'
              used={usage.storageBytes}
              cap={limitFor('assetStorageBytes')}
              format={formatBytes}
              testId='billing-usage-storage'
            />
          </div>
        </section>

        <section data-testid='billing-plans'>
          <h2 className='mb-2 text-sm font-semibold uppercase tracking-wider text-dm-text-accent'>
            Plans
          </h2>
          {!staffUncapped && !shopifyBilled && !paidPlanChangesViaPortal && (
            <p className='max-w-2xl text-xs text-dm-text-accent'>
              Paid plans are billed monthly through Stripe. Upgrading opens a
              secure Stripe checkout page; your plan changes once payment is
              confirmed.
            </p>
          )}
          {paidPlanChangesViaPortal && (
            <p
              data-testid='billing-plan-change-note'
              className='max-w-2xl text-xs text-dm-text-accent'
            >
              To upgrade or downgrade, use the Manage billing button above
              Stripe switches your plan and charges only the prorated
              difference.
            </p>
          )}
          {checkoutError !== null && (
            <div className='mt-3 max-w-2xl' data-testid='billing-checkout-error'>
              <ErrorInline onDismiss={() => setCheckoutError(null)}>
                {checkoutError}
              </ErrorInline>
            </div>
          )}
          {plans.length > 0
            ? (
              <div className='mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
                {plans
                  .filter((p) => p.purchasable || p.tier === tier)
                  .map((p) => {
                    const offerUpgrade = status === 'ready' &&
                      !staffUncapped && !shopifyBilled &&
                      !paidPlanChangesViaPortal &&
                      p.purchasable && p.tier !== tier
                    const offerPortalUpgrade = status === 'ready' &&
                      !staffUncapped && paidPlanChangesViaPortal &&
                      p.purchasable && p.tier !== tier
                    const offerDowngradeToFree = status === 'ready' &&
                      !staffUncapped && paidPlanChangesViaPortal &&
                      p.tier === 'free'
                    const anyRedirectBusy = checkoutBusyTier !== null || portalBusySource !== null
                    let cta: PlanCardCta | null = null
                    if (offerUpgrade) {
                      cta = {
                        label: 'Upgrade',
                        busy: checkoutBusyTier === p.tier,
                        disabled: anyRedirectBusy && checkoutBusyTier !== p.tier,
                        busyLabel: 'Opening checkout…',
                        testId: `billing-upgrade-${p.tier}`,
                        handleClick: () => { void handleUpgrade(p.tier) }
                      }
                    } else if (offerDowngradeToFree) {
                      cta = {
                        label: 'Downgrade',
                        busy: portalBusySource === 'downgrade-free',
                        disabled: anyRedirectBusy && portalBusySource !== 'downgrade-free',
                        busyLabel: 'Opening billing portal…',
                        title: 'Downgrading means cancelling your subscription: the change itself happens in the Stripe billing portal.',
                        testId: 'billing-downgrade-free',
                        handleClick: () => { void handleManageBilling('downgrade-free') }
                      }
                    } else if (offerPortalUpgrade) {
                      cta = {
                        label: 'Upgrade',
                        busy: portalBusySource === `plan-${p.tier}`,
                        disabled: anyRedirectBusy && portalBusySource !== `plan-${p.tier}`,
                        busyLabel: 'Opening billing portal…',
                        title: 'Plan changes happen in the Stripe billing portal: we open it for you.',
                        testId: `billing-upgrade-${p.tier}`,
                        handleClick: () => { void handleManageBilling(`plan-${p.tier}`) }
                      }
                    }
                    return (
                      <PlanCard
                        key={p.tier}
                        plan={p}
                        current={p.tier === tier}
                        cta={cta}
                      />
                    )
                  })}
              </div>
              )
            : (
              <p className='mt-4 text-sm text-dm-text-secondary'>
                Plan details are not available right now.
              </p>
              )}
        </section>

      </div>
    </BillingShell>
  )
}
